import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import { isSameMonth } from 'date-fns/isSameMonth';
import { isValid } from 'date-fns/isValid';

import { parseDateTime } from './localisation';

import type { DateString } from '@web-platform/shared-universal-types/src/dateString';

const DATE_REGEX = /^(20\d{2})-(\d{2})-(\d{2})$/;
const MONTH_REGEX = /^(20\d{2})-(\d{2})$/;

export const isPastDay = (date: DateString) =>
  isBefore(parseDateTime(date), new Date()) && !isSameDay(parseDateTime(date), new Date());

export const isPastMonth = (date: DateString) =>
  isBefore(parseDateTime(date), new Date()) && !isSameMonth(parseDateTime(date), new Date());

export const getIsDifferentMonth = (initialMonth: string, finalMonth: string): boolean =>
  !!initialMonth && !!finalMonth && initialMonth !== finalMonth;

export const validDate = (date: unknown): date is Date =>
  !!date && date !== 'Invalid date' && typeof date === 'string' && isValid(parseDateTime(date));

/*
- Empty string ('') represents ANYTIME date inside webapp for Combined Explore
- 'anytime' represents ANYTIME date inside server
*/
export const checkIsAnytime = (date: unknown, isAnytime?: boolean): boolean =>
  (date === '' && isAnytime === true) || date === 'anytime';

export const DATE_TYPE = {
  ANYTIME: 'ANYTIME', // ''
  MONTH: 'MONTH', // yyyy-MM
  SPECIFIC_DATE: 'SPECIFIC_DATE', // yyyy-MM-dd
  INVALID_DATE: 'INVALID_DATE', // other invalid date
} as const;

export const isDateString = (date: string | undefined): date is DateString =>
  getDateType(date) !== DATE_TYPE.INVALID_DATE;

// Only determine the type of the input date, does not include determining whether it is a past date.
// Acceptable date pattern: '' or yyyy-MM or yyyy-MM-dd
export const getDateType = (date: unknown, isAnytime?: boolean) => {
  // '' - anytime
  if (checkIsAnytime(date, isAnytime)) return DATE_TYPE.ANYTIME;

  // yyyy-MM - month
  if (validDate(date) && date && typeof date === 'string' && MONTH_REGEX.test(date))
    return DATE_TYPE.MONTH;

  // yyyy-MM-dd - specific date
  if (validDate(date) && date && typeof date === 'string' && DATE_REGEX.test(date))
    return DATE_TYPE.SPECIFIC_DATE;

  // others - invalid date
  return DATE_TYPE.INVALID_DATE;
};
